import { useState } from 'react';
import type { NodeDisplay } from '@integrate/contracts';
import {
  analyticsTabs,
  defaultCoverageLevels,
  type AnalyticsTab,
  type CoverageLevel,
  type MetricKey,
  type Perspective,
  type SeparationState,
} from '@integrate/core';
import { ComparePane } from './ComparePane.js';
import { SweepPane } from './SweepPane.js';
import { VariancePane } from './VariancePane.js';
import { SpansLayersPane } from './SpansLayersPane.js';
import { StrandedPane } from './StrandedPane.js';
import { SummaryPane } from './SummaryPane.js';
import { PhaseBridgePane } from './PhaseBridgePane.js';
import { LevelPane } from './LevelPane.js';
import { DedicationPane } from './DedicationPane.js';
import { CoverageMatrixPane } from './CoverageMatrixPane.js';
import { PerspectivePane } from './PerspectivePane.js';
import { Workbench } from '../ui/Workbench.js';

/**
 * Whether a tab has a pane. One predicate, so the tab and the "not ported yet" note cannot disagree —
 * and it has to be a predicate rather than a list because the level tabs are generated per project.
 */
/*
  Every named tab has a pane now, and every level tab goes to `LevelPane` — so this is a total function
  and the "not ported yet" branch below is unreachable. Kept as a predicate rather than deleted: the tab
  set is generated per project, and a future tab arriving without a pane should say so rather than render
  an empty box.
*/
const NAMED_PORTED: AnalyticsTab[] = [
  'summary',
  'phase-bridge',
  'by-scenario',
  'coverage-matrix',
  'split-heads',
  'stranded',
  'spans-layers',
  'compare-scenarios',
  'threshold-sweep',
  'variance',
];
const isPorted = (tab: AnalyticsTab): boolean =>
  NAMED_PORTED.includes(tab) || tab.startsWith('level-');

/**
 * The analytics modal: read-only panes, one tab each.
 *
 * The app's version has seven named tabs plus one per grouping level. This ships the ones whose compute
 * functions exist and are parity-checked, and the shell is here rather than deferred for one
 * reason: every entry point into this modal in the app is a DEEP link. The RemainCo panel opens the
 * stranded tab by name; a group's restructure pane opens the coverage matrix. A modal whose tab cannot be
 * named from the caller is a control that lands somewhere other than where its label says — the defect
 * P4i-d and P4i-f exist to close — so the tab is a parameter from the first tab, not from the second.
 *
 * EVERY NAMED TAB NOW HAS A PANE — including, at last, the scenario comparison, whose absence this
 * header used to record. The store still holds one design at a time; what changed is the transport:
 * `/scenario-compare` prices every sibling server-side with the deck slide's own engine calls, and
 * `ComparePane` renders the rows. Two panes still ship without a control the app has, for the
 * remaining half of the same reason: the phase bridge and the perspective comparison both had a
 * scenario PICKER, which needs a sibling's whole DESIGN in memory — priced rows are not a design,
 * and a picker that silently compared the design in memory against another scenario's name would be
 * worse than no picker. That half stays recorded rather than faked.
 *
 * A pane is never a command. Nothing here takes `onDispatch`, and nothing here calls `setLens`: a
 * read-only surface that narrowed the chart behind it — and wrote that narrowing to the preferences row,
 * as `filters` does — would have a lasting side effect on everything else the user sees.
 */
/*
  The tab set is a function of the project, not a constant: there is one pane per GROUPING LEVEL as well
  as the named ones, and levels are added, removed and reordered by command. `analyticsTabs` and
  `resolveAnalyticsTab` live in core beside the phase spine's equivalent, with the ordering, the fallback
  and the zero-level case tested there rather than only through the browser.
*/
export type { AnalyticsTab } from '@integrate/core';

export function AnalyticsModal({
  state,
  scenarioName,
  projectId,
  activeScenarioId,
  scenarioCount,
  chartPerspective,
  statusColors,
  tab,
  onTab,
  onOpenPlan,
  onOpenAssumptions,
  onClose,
}: {
  state: SeparationState;
  scenarioName: string;
  /** For the compare pane's fetch — the one pane whose numbers cannot come from `state`. */
  projectId: string;
  /** Which scenario the screen behind this modal shows, so the compare pane can mark its row. */
  activeScenarioId: string;
  /** How many scenarios the project has. `state` holds one design and cannot know its siblings. */
  scenarioCount: number;
  /** Where the chart is. Read ONCE, as the level pane's initial perspective — never as a live input. */
  chartPerspective: Perspective;
  /** The card palette, so a bucket is one colour everywhere. A pane never picks its own. */
  statusColors: NodeDisplay['colors']['status'];
  tab: AnalyticsTab;
  onTab(tab: AnalyticsTab): void;
  onOpenPlan(): void;
  onOpenAssumptions(): void;
  onClose(): void;
}): JSX.Element {
  /*
    THE PANES' CONTROLS LIVE HERE, not in the panes.

    Tabs render conditionally, so switching tab unmounts the pane — and every control held in pane-local
    state was discarded with it. Pick Cost on the phase bridge, glance at the stranded pane, come back:
    headcount. Worse on the level breakdown, whose perspective was seeded from the CHART, so a remount
    silently replaced the reader's own choice with the chart's and changed every number on the page. The
    frozen app kept all of these at module scope (`_pbMetric`, `_strMetric`, `_strLevelIdx`) precisely so
    they survived a pane re-render.

    Held for as long as the modal is open, which also makes "read the chart's perspective ONCE" true:
    once per opening rather than once per remount.
  */
  const [metric, setMetric] = useState<MetricKey>('hc');
  const [strandedMetric, setStrandedMetric] = useState<'cost' | 'fte'>('cost');
  const [levelPerspective, setLevelPerspective] = useState<Perspective>(chartPerspective);
  // Its own state, not `levelPerspective` shared: reading spans on RemainCo while breaking headcount
  // down on Carve-out is a normal minute of work, and one toggle silently moving the other pane's
  // numbers is the exact defect these modal-held controls exist to prevent.
  const [spansPerspective, setSpansPerspective] = useState<Perspective>(chartPerspective);
  // The Sankey's own metric, like the frozen app's `_sankeyMetric` — module scope there, modal scope
  // here, surviving tab switches either way.
  const [sankeyMetric, setSankeyMetric] = useState<MetricKey>('hc');
  const [strandedLevel, setStrandedLevel] = useState<string | null>(
    state.groupingLevels[0]?.key ?? null,
  );
  const [dedicationLevel, setDedicationLevel] = useState<string | null>(
    state.groupingLevels[0]?.key ?? null,
  );
  // Seeded from the project, then reconciled against the available fields on every render of the pane.
  const [coverageLevels, setCoverageLevels] = useState<CoverageLevel[]>(() =>
    defaultCoverageLevels(state),
  );
  const [coverageSide, setCoverageSide] = useState<'remain' | 'carve-out'>('carve-out');
  // The app's default for this one: the second grouping level, or the only one.
  const [perspectiveLevel, setPerspectiveLevel] = useState<string | null>(
    () => state.groupingLevels[Math.min(1, state.groupingLevels.length - 1)]?.key ?? null,
  );
  // The compare pane's headcount weight. hc/fte only — its money columns are always drawn.
  const [compareMetric, setCompareMetric] = useState<'hc' | 'fte'>('hc');

  return (
    <Workbench
      testId="analytics-modal"
      title="Analytics — cost & headcount"
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button onClick={onClose}>Done</button>
        </>
      }
    >
      <div className="tabs" role="tablist" aria-label="Analytics panes">
        {analyticsTabs(state, { scenarioCount }).map(({ key, label, levelKey }) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            data-testid={`analytics-tab-${key}`}
            /* So an e2e can address "the second level's tab" without knowing the level's key. */
            data-level-key={levelKey}
            onClick={() => onTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab.startsWith('level-') && (
        <LevelPane
          state={state}
          tab={tab}
          perspective={levelPerspective}
          onPerspective={setLevelPerspective}
        />
      )}
      {tab === 'summary' && (
        <SummaryPane
          state={state}
          scenarioName={scenarioName}
          sankeyMetric={sankeyMetric}
          onSankeyMetric={setSankeyMetric}
          onOpenAssumptions={onOpenAssumptions}
        />
      )}
      {tab === 'phase-bridge' && (
        <PhaseBridgePane
          state={state}
          scenarioName={scenarioName}
          metric={metric}
          onMetric={setMetric}
          onOpenAssumptions={onOpenAssumptions}
        />
      )}
      {tab === 'by-scenario' && (
        <PerspectivePane
          state={state}
          scenarioName={scenarioName}
          levelKey={perspectiveLevel}
          onLevelKey={setPerspectiveLevel}
        />
      )}
      {tab === 'coverage-matrix' && (
        <CoverageMatrixPane
          state={state}
          metric={metric}
          onMetric={setMetric}
          levels={coverageLevels}
          onLevels={setCoverageLevels}
          perspective={coverageSide}
          onPerspective={setCoverageSide}
        />
      )}
      {tab === 'split-heads' && (
        <DedicationPane
          state={state}
          colors={statusColors}
          metric={metric}
          onMetric={setMetric}
          levelKey={dedicationLevel}
          onLevelKey={setDedicationLevel}
        />
      )}
      {tab === 'spans-layers' && (
        <SpansLayersPane
          state={state}
          perspective={spansPerspective}
          onPerspective={setSpansPerspective}
        />
      )}
      {tab === 'threshold-sweep' && <SweepPane state={state} />}
      {tab === 'variance' && (
        <VariancePane
          state={state}
          projectId={projectId}
          scenarioId={activeScenarioId}
          scenarioName={scenarioName}
        />
      )}
      {tab === 'compare-scenarios' && (
        <ComparePane
          projectId={projectId}
          activeScenarioId={activeScenarioId}
          scenarioCount={scenarioCount}
          metric={compareMetric}
          onMetric={setCompareMetric}
        />
      )}
      {tab === 'stranded' && (
        <StrandedPane
          state={state}
          metric={strandedMetric}
          onMetric={setStrandedMetric}
          levelKey={strandedLevel}
          onLevelKey={setStrandedLevel}
          onOpenPlan={onOpenPlan}
          onOpenAssumptions={onOpenAssumptions}
        />
      )}
      {/*
        A tab whose pane has not landed yet says so, rather than rendering an empty box. The tab set
        is complete from the first sub-milestone on purpose: panes arrive into a bar that does not
        move under the reader, and the alternative — adding tabs as they land — reorders the row
        every time.
      */}
      {!isPorted(tab) && (
        <p className="muted" data-testid={`analytics-pending-${tab}`}>
          This pane has not been ported yet — its numbers need a compute function in core and a
          parity case before anything draws them.
        </p>
      )}
    </Workbench>
  );
}
